import { useCallback, useEffect, useRef, useState } from 'react';
import { api, fetchPreview, type Entitlement, type Programme, type Screen, type Venue, type VenueOrder, type VenueOrders, type VenueOverview } from './client';
import { subscribeLive, subscribeScreenEvents } from './live';

/** A clock that ticks every second, for "last heartbeat 4s ago" and match clocks. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/**
 * Runs `load` now and every `intervalMs`; exposes `refresh` for event-driven reloads.
 * The first dep is the thing being shown (a venue): only a change of it clears the data,
 * so a reload updates the page in place instead of blanking it.
 */
function usePolled<T>(load: (() => Promise<T | undefined>) | null, intervalMs: number, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [failed, setFailed] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;
  const shownRef = useRef<unknown>(deps[0]);

  const refresh = useCallback(async () => {
    const fn = loadRef.current;
    if (!fn) return;
    try {
      const value = await fn();
      if (value !== undefined) setData(value);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    if (shownRef.current !== deps[0]) {
      shownRef.current = deps[0];
      setData(null);
    }
    if (!load) return;
    void refresh();
    const timer = setInterval(refresh, intervalMs);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, failed, refresh, setData };
}

export function useVenues() {
  const { data, failed } = usePolled<Venue[]>(async () => (await api.GET('/v1/venues')).data, 300_000, []);
  return { venues: data, error: failed };
}

/**
 * Screens for a venue, kept live: refetched within ~150ms of any screen event on the
 * server stream, with polling as a fallback if the stream is down.
 */
export function useLiveScreens(venueId: string | undefined) {
  const polled = usePolled<Screen[]>(
    venueId ? async () => (await api.GET('/v1/venues/{venueId}/screens', { params: { path: { venueId } } })).data : null,
    10_000,
    [venueId],
  );
  const [live, setLive] = useState(false);
  const { refresh, setData } = polled;

  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | null = null;
    const stop = subscribeScreenEvents(
      () => {
        // Coalesce bursts (a "tune all" fires one event per screen) into one refetch.
        if (!pending) pending = setTimeout(() => ((pending = null), void refresh()), 150);
      },
      (connected) => {
        setLive(connected);
        if (connected) void refresh();
      },
    );
    return () => {
      stop();
      if (pending) clearTimeout(pending);
    };
  }, [refresh]);

  const update = useCallback((screen: Screen) => setData((prev) => prev?.map((s) => (s.id === screen.id ? screen : s)) ?? prev), [setData]);
  const replaceAll = useCallback((next: Screen[]) => setData(next), [setData]);

  return { screens: polled.data, offline: polled.failed, live, refresh, update, replaceAll };
}

/**
 * The venue's figures (screens online, fixtures, QR scans, orders), kept live: refetched
 * when a screen connects, disconnects, is paired or changes what it plays, and on every
 * order or QR scan at the venue. Heartbeats do not refresh it; polling is the fallback.
 */
export function useOverview(venueId: string | undefined) {
  const polled = usePolled<VenueOverview>(
    venueId ? async () => (await api.GET('/v1/venues/{venueId}/overview', { params: { path: { venueId } } })).data : null,
    30_000,
    [venueId],
  );
  const { refresh } = polled;
  useEffect(() => {
    if (!venueId) return;
    let pending: ReturnType<typeof setTimeout> | null = null;
    const soon = () => {
      if (!pending) pending = setTimeout(() => ((pending = null), void refresh()), 200);
    };
    const stop = subscribeLive(
      (e) => {
        if (e.kind === 'screen' ? e.event.reason !== 'heartbeat' : e.event.venueId === venueId) soon();
      },
      (connected) => connected && soon(),
    );
    return () => {
      stop();
      if (pending) clearTimeout(pending);
    };
  }, [venueId, refresh]);
  return polled;
}

export function useProgramme(venueId: string | undefined) {
  return usePolled<Programme>(
    venueId ? async () => (await api.GET('/v1/venues/{venueId}/programme', { params: { path: { venueId } } })).data : null,
    20_000,
    [venueId],
  );
}

export function useEntitlements(venueId: string | undefined) {
  return usePolled<Entitlement[]>(
    venueId ? async () => (await api.GET('/v1/venues/{venueId}/entitlements', { params: { path: { venueId } } })).data : null,
    300_000,
    [venueId],
  );
}

/** Latest preview frame for a screen, refetched whenever the player uploads a new one. */
export function usePreview(screenId: string, capturedAt: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!capturedAt) return;
    let cancelled = false;
    fetchPreview(screenId).then((next) => {
      if (!cancelled && next) setUrl(next); // the effect below revokes the previous URL
    });
    return () => {
      cancelled = true;
    };
  }, [screenId, capturedAt]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  return url;
}

/**
 * A venue's Nectar order queue, kept live: refetched on any order event for the venue,
 * with polling as a fallback. `enabled` is false for users without orders.manage.
 */
export function useOrders(venueId: string | undefined, enabled: boolean) {
  const polled = usePolled<VenueOrders>(
    venueId && enabled ? async () => (await api.GET('/v1/venues/{venueId}/orders', { params: { path: { venueId } } })).data : null,
    15_000,
    [venueId, enabled],
  );
  const [live, setLive] = useState(false);
  const { refresh, setData } = polled;

  useEffect(() => {
    if (!venueId || !enabled) return;
    const stop = subscribeLive(
      (e) => e.kind === 'order' && e.event.venueId === venueId && void refresh(),
      (connected) => {
        setLive(connected);
        if (connected) void refresh();
      },
    );
    return stop;
  }, [venueId, enabled, refresh]);

  /** Applies a changed order locally, moving it between open and done. */
  const apply = useCallback(
    (order: VenueOrder) =>
      setData((prev) => {
        if (!prev) return prev;
        const open = prev.open.filter((o) => o.id !== order.id);
        const done = prev.done.filter((o) => o.id !== order.id);
        return order.status === 'new' || order.status === 'preparing'
          ? { ...prev, open: [...open, order].sort((x, y) => x.placedAt.localeCompare(y.placedAt)), done }
          : { ...prev, open, done: [order, ...done] };
      }),
    [setData],
  );

  return { orders: polled.data, offline: polled.failed, live, refresh, apply };
}
